import { Box } from '@rebass/grid';
import { gql } from '@apollo/client';
import { useRouter } from 'next/router';
import { stringifyUrl } from 'query-string';
import React, { FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Booking } from '../../entities/Booking';
import NotFoundError from '../../entities/NotFoundError';
import { usePlannerPageRentalUnitLazyQuery } from '../../generated/graphql';
import usePlannerPeriod from '../../hooks/planner/usePlannerPeriod';
import useQueryParams from '../../hooks/useQueryParams';
import { convertToPeriod, getDateStringsFromTrip, withDefaultBookingValues } from '../../utils/trip';
import PlannerView, { Container as PlannerContainer, Header } from '../TripPlanner';
import bookingParamsConfig from '../../constants/bookingParams';
import Head from 'next/head';
import { useTenantContext } from '../../context/TenantContext';
import baseUrls from '../../constants/baseUrls';
import Cookies from 'js-cookie';
import { TRAVELBASE_AFF, TRAVELBASE_AFF_REFERENCE } from '../../constants/affiliate';
import withAuth from '../../hoc/withAuth';

export const query = gql`
    query PlannerPageRentalUnit($slug: String!) {
        rentalUnit(slug: $slug) {
            id
            brand
            maxOccupancy
            petsAllowed
        }
    }
`;

const Planner: FC<React.PropsWithChildren<unknown>> = () => {
    const { query: params, isReady, locale } = useRouter();
    const [bookingParams] = useQueryParams(bookingParamsConfig);
    const plannerPeriodProps = usePlannerPeriod(
        convertToPeriod(bookingParams.arrivalDate, bookingParams.departureDate)
    );
    const { brandConfig } = useTenantContext();
    const [booking, setBooking] = useState<Booking>({
        ...bookingParams,
        amountAdults: bookingParams.amountAdults ?? 2,
    });
    const [fetch, { called, data, loading }] = usePlannerPageRentalUnitLazyQuery({
        variables: { slug: params.slug as string },
    });
    if (called && !loading && !data?.rentalUnit?.id) {
        throw new NotFoundError();
    }

    useEffect(() => {
        if (!called && isReady) {
            fetch();
        }
    }, [called, fetch, isReady]);

    const onSubmit = useCallback(() => {
        const { amountAdults, amountBabies, amountChildren, amountPets, amountYouths, trip } =
            withDefaultBookingValues(booking);

        if (!trip || !data?.rentalUnit?.id || !window.top) {
            return;
        }

        const { arrivalDate, departureDate } = getDateStringsFromTrip(trip);
        const affiliateHandle = Cookies.get(TRAVELBASE_AFF);
        const affiliateReference = Cookies.get(TRAVELBASE_AFF_REFERENCE);
        if (affiliateReference) {
            params.affiliateReference = affiliateReference;
        }
        if (affiliateHandle) {
            params.affiliateHandle = affiliateHandle;
        }
        window.top.location.href = stringifyUrl({
            url: brandConfig.checkout?.checkoutShouldRedirect
                ? `${baseUrls[data?.rentalUnit?.brand as keyof typeof baseUrls] ?? ''}/${locale}/checkout/redirect`
                : `${process.env.NEXT_PUBLIC_BASE_URL ?? ''}/${locale}/checkout/redirect`,
            query: {
                amountAdults: amountAdults.toString(),
                amountBabies: amountBabies.toString(),
                amountChildren: amountChildren.toString(),
                amountPets: amountPets.toString(),
                amountYouth: amountYouths.toString(),
                arrivalDate: arrivalDate.toString(),
                departureDate: departureDate.toString(),
                specialId: trip.specialId ?? undefined,
                rentalUnitSlug: params.slug,
                rentalUnitId: data?.rentalUnit?.id,
                ...params,
            },
        });
    }, [
        booking,
        brandConfig.checkout?.checkoutShouldRedirect,
        data?.rentalUnit?.brand,
        data?.rentalUnit?.id,
        params,
        locale,
    ]);

    const onBookingChange = useCallback((value: Booking) => setBooking(current => ({ ...current, ...value })), []);
    if (!isReady) {
        return null;
    }

    return (
        <>
            <Head>
                {brandConfig.planner?.styleSheetUrl && (
                    <link rel="stylesheet" href={brandConfig.planner.styleSheetUrl} type="text/css" />
                )}
            </Head>
            <Container>
                <PlannerWrapper>
                    <PlannerView
                        {...plannerPeriodProps}
                        booking={booking}
                        setBooking={onBookingChange}
                        slug={params.slug as string}
                        maxOccupancy={data?.rentalUnit?.maxOccupancy}
                        petsAllowed={data?.rentalUnit?.petsAllowed}
                        hideExplanation
                        iframe
                        onSubmit={onSubmit}
                        hasInlineReservationBar
                    />
                </PlannerWrapper>
            </Container>
        </>
    );
};

const Container = styled.div`
    position: relative;
    padding: 1px;
`;

const PlannerWrapper = styled(Box)`
    @media screen and (max-width: ${({ theme }) => theme.mediaQueriesValues.s - 1}px) {
        height: 100%;
        ${PlannerContainer} {
            margin-top: 0;
            ${Header} {
                padding-top: 0.8rem;
                position: sticky;
                top: 0;
                background: white;
                z-index: 1;
                box-shadow: 0 1.6rem 2.4rem 0 rgba(59, 118, 160, 0.03), 0 2.4rem 8rem 0 rgba(59, 118, 160, 0.05);
            }
        }
    }
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        ${PlannerContainer} {
            padding-bottom: calc(var(--booking-bar-height) + 3.2rem);
        }
    }
`;
export default withAuth(Planner, {
    onFail: () => 'ignore',
    withLoadingProp: true,
});
